import React, { useState, useEffect } from "react";
import { Download, Plus, X, Eye } from "lucide-react";
import InvoiceForm from "./InvoiceForm";
import InvoiceTemplate from "../../../components/AdminPortal/Finance/InvoiceTemplate";
import { getInvoices,createInvoice } from "../../../services/AdminPortal/invoiceAPI";

const InvoiceManagement = () => {
  const [showFormModal, setShowFormModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const data = await getInvoices();
      setInvoices(data || []);
    } catch (err) {
      console.error("Fetch invoices error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setShowFormModal(true);
  };

  const handleCreate = async (payload) => {
    try {
      console.log("Payload:", payload);
      await createInvoice(payload);
      setShowFormModal(false);
      await fetchInvoices();
    } catch (err) {
      console.error("Create invoice error:", err);
      toast.error("Failed to create invoice!");
    }
  };

  const handleView = (invoice) => {
    setSelectedInvoice(invoice);
    setShowViewModal(true);
  };

  const handleDownload = async (invoice) => {
    // use template to download via print or external library from InvoiceTemplate DOM,
    // We will use InvoiceTemplate's static export helper if you want to implement.
    // For now, open View modal then use the Download button available there.
    setSelectedInvoice(invoice);
    setShowViewModal(true);
  };

  return (
    <div className="p-6">
      <div className="bg-[#131313] border border-[#333] p-4 rounded-lg w-full">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white text-sm font-semibold">
            INVOICE MANAGEMENT
          </h2>
          <button
            onClick={handleAddClick}
            className="bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-semibold px-3 py-1 rounded flex items-center gap-1"
          >
            <Plus size={14} /> Add Invoice
          </button>
        </div>

        {loading ? (
          <div className="text-center text-gray-400 py-8">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px] text-gray-200">
              <thead>
                <tr className="text-gray-400 text-[11px] border-b border-[#2a2a2a]">
                  <th className="text-left pb-2">INVOICE</th>
                  <th className="text-left pb-2">CLIENT</th>
                  <th className="text-right pb-2">AMOUNT</th>
                  <th className="text-center pb-2">DUE DATE</th>
                  <th className="text-center pb-2">STATUS</th>
                  <th className="text-center pb-2">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv, idx) => (
                  <tr
                    key={inv._id || idx}
                    className="border-b border-[#2a2a2a]"
                  >
                    <td className="py-2">{inv.invoiceNumber}</td>
                    <td className="py-2">{inv.billedTo?.name}</td>
                    <td className="text-right py-2">
                      ₹
                      {Number(
                        inv.totals?.totalInvoiceValue || 0
                      ).toLocaleString("en-IN")}
                    </td>
                    <td className="text-center py-2">
                      {inv.dueDate
                        ? new Date(inv.dueDate).toLocaleDateString("en-GB")
                        : "-"}
                    </td>
                    <td className="text-center py-2">
                      <div className="flex flex-col gap-1">
                        {inv.fees?.map((fee, i) => (
                          <span
                            key={i}
                            className={`px-2 py-1 rounded-full text-xs ${
                              fee.status === "Paid"
                                ? "bg-green-900 text-green-400"
                                : fee.status === "Overdue"
                                ? "bg-red-900 text-red-400"
                                : "bg-yellow-900 text-yellow-400"
                            }`}
                          >
                            {fee.description}: {fee.status}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="text-center py-2">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => handleView(inv)}
                          className="text-[#FFCA20] text-xs hover:underline flex items-center gap-1"
                        >
                          <Eye size={12} /> View
                        </button>
                        <button
                          onClick={() => handleDownload(inv)}
                          className="text-gray-400 text-xs hover:underline flex items-center gap-1"
                        >
                          <Download size={12} /> Download
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {invoices.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center text-gray-400 py-8">
                      No invoices found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Invoice Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-[#131313] p-6 rounded-lg w-full max-w-4xl shadow-lg border border-[#333]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white text-base font-semibold">
                Add Invoice
              </h3>
              <button
                onClick={() => setShowFormModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            <InvoiceForm
              onCancel={() => setShowFormModal(false)}
              onSave={handleCreate}
            />
          </div>
        </div>
      )}

      {/* View Invoice Modal */}
      {showViewModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-[#131313] p-6 rounded-lg w-full max-w-3xl shadow-lg border border-[#333] max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white text-lg font-semibold">
                Invoice Preview
              </h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <InvoiceTemplate
              invoice={selectedInvoice}
              allowDownload={true}
              onClose={() => setShowViewModal(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceManagement;
